import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { TabViewModule } from 'primeng/tabview';
import { InputNumberModule } from 'primeng/inputnumber';
import { PanelModule } from 'primeng/panel';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { ProgressBarModule } from 'primeng/progressbar';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { CapitalizeWordsPipe } from 'src/app/pipes/capitalize-words.pipe';
import { OutreachService } from 'src/app/services/outreach.service';
import { ProspectService } from 'src/app/services/prospect.service';
import { JobService, JobStatus } from 'src/app/services/job.service';
import { UserSettingsService } from 'src/app/services/user-settings.service';
import { AuthService } from 'src/app/services/auth.service';
import { TemplateValidationService } from 'src/app/services/template-validation.service';
import { EmailSequence, NewSequence, SequenceStep, TemplateModel, TimeUnit } from 'src/app/models/email-sequence.model';
import { Integration } from 'src/app/models/integration.model';
import { Contact } from 'src/app/models/message.model';
import { SelectableCompany } from 'src/app/models/company.model';
import { CreateEmailSequenceResponse, ProspectListsResponse } from 'src/app/models/api-response.model';
import { UserSettings } from 'src/app/models/user-settings.model';
import { LeadDistributionStatusComponent } from '../../common/lead-distribution-status/lead-distribution-status.component';
import { TemplateConversionDialogComponent } from '../../dialogs/template-conversion-dialog/template-conversion-dialog.component';

@Component({
  selector: 'app-new-email-sequence',
  templateUrl: './new-email-sequence.component.html',
  styleUrls: ['./new-email-sequence.component.scss'],
  standalone: true,
  providers: [DialogService],
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    CheckboxModule,
    TabViewModule,
    InputNumberModule,
    PanelModule,
    MultiSelectModule,
    SelectModule,
    ProgressBarModule,
    DynamicDialogModule,
    CapitalizeWordsPipe,
    LeadDistributionStatusComponent
  ]
})
export class NewEmailSequenceComponent implements OnInit, OnDestroy, OnChanges {
  @Input() visible = false;
  @Input() editingSequence = false;
  @Input() currentCompany: SelectableCompany | null = null;
  @Input() integrations: Integration[] = [];
  @Input() templates: TemplateModel[] = [];
  @Input() prospects: Contact[] = [];
  @Input() emailTemplates: { label: string; value: string }[] = [];
  @Input() selectedIntegration: Integration | null = null;
  @Input() platformEmailAccounts: any[] = [];
  @Input() sequence: EmailSequence | null = null;

  @Output() sequenceSaved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() integrationChanged = new EventEmitter<Integration>();
  @Output() prospectsLoaded = new EventEmitter<Contact[]>();

  // Form state
  newSequence: NewSequence = {
    sequenceId: '',
    name: '',
    emailAccounts: [],
    description: '',
    industry: '',
    prospectListId: '',
    steps: [
      {
        type: 'initial',
        templateId: null,
        abTestingEnabled: false
      }
    ],
    integration: ''
  };

  selectedPlatformEmailAccounts: any[] = [];
  hasTriedToSave = false;
  isSaving = false;
  loadingMessage = '';
  prospectsLoading = false;
  
  // Lead distribution tracking
  leadDistributionJobId: string | null = null;
  showLeadDistribution = false;
  sequenceCreated = false;
  leadDistributionTimeout: any = null;
  readonly LEAD_DISTRIBUTION_TIMEOUT_MS = 300000; // 5 minutes
  
  // Progress tracking for lead distribution
  jobProgress = 0;
  jobMessage = '';
  jobStatus: 'queued' | 'processing' | 'completed' | 'failed' = 'queued';

  // Save progress tracking
  saveSteps = [
    { label: 'Validating', description: 'Checking sequence configuration', completed: false, active: false },
    { label: 'Creating Campaign', description: 'Setting up campaign on platform', completed: false, active: false },
    { label: 'Configuring', description: 'Adding email accounts and settings', completed: false, active: false },
    { label: 'Finalizing', description: 'Saving sequence to database', completed: false, active: false }
  ];
  currentSaveStep = 0;

  timeUnits: TimeUnit[] = [
    { label: 'Days', value: 'days' }
  ];

  industries = [
    { label: 'Technology', value: 'technology' },
    { label: 'Software', value: 'software' },
    { label: 'Healthcare', value: 'healthcare' },
    { label: 'Finance & Banking', value: 'finance' },
    { label: 'Insurance', value: 'insurance' },
    { label: 'Real Estate', value: 'real_estate' },
    { label: 'Manufacturing', value: 'manufacturing' },
    { label: 'Retail & E-commerce', value: 'retail' },
    { label: 'Consulting', value: 'consulting' },
    { label: 'Marketing & Advertising', value: 'marketing' },
    { label: 'Education', value: 'education' },
    { label: 'Government', value: 'government' },
    { label: 'Non-profit', value: 'nonprofit' },
    { label: 'Legal Services', value: 'legal' },
    { label: 'Accounting', value: 'accounting' },
    { label: 'Construction', value: 'construction' },
    { label: 'Transportation & Logistics', value: 'transportation' },
    { label: 'Energy & Utilities', value: 'energy' },
    { label: 'Telecommunications', value: 'telecommunications' },
    { label: 'Hospitality & Tourism', value: 'hospitality' },
    { label: 'Food & Beverage', value: 'food_beverage' },
    { label: 'Media & Entertainment', value: 'media' },
    { label: 'Pharmaceuticals', value: 'pharmaceuticals' },
    { label: 'Automotive', value: 'automotive' },
    { label: 'Aerospace & Defense', value: 'aerospace' },
    { label: 'Agriculture', value: 'agriculture' },
    { label: 'Mining', value: 'mining' },
    { label: 'Sports & Recreation', value: 'sports' },
    { label: 'Beauty & Personal Care', value: 'beauty' },
    { label: 'Other', value: 'other' }
  ];

  constructor(
    private messageService: MessageService,
    private dialogService: DialogService,
    private templateValidationService: TemplateValidationService,
    private emailOutreachService: OutreachService,
    private prospectService: ProspectService,
    private jobService: JobService,
    private userSettingsService: UserSettingsService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadProspectsIfNeeded();
    
    
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    // Check user settings when dialog becomes visible
    if (changes['visible'] && changes['visible'].currentValue && !changes['visible'].previousValue) {
      await this.checkUserSettings();
    }
    
    // Existing ngOnChanges logic
    if (changes['sequence'] || changes['editingSequence']) {
      this.initializeForm();
    }
    
    // If prospects change and we're in edit mode, update the prospect selection
    if (changes['prospects'] && this.editingSequence && this.sequence) {
      this.updateProspectSelection();
    }
  }

  ngOnDestroy(): void {
    // Clear timeout if component is destroyed
    if (this.leadDistributionTimeout) {
      clearTimeout(this.leadDistributionTimeout);
      this.leadDistributionTimeout = null;
    }
    
    // Job polling is handled by LeadDistributionStatusComponent
    
    // Stop polling in JobService
    if (this.leadDistributionJobId) {
      this.jobService.stopPolling(this.leadDistributionJobId);
    }
  }

  private updateProspectSelection(): void {
    // If we have a prospectListId (string), find the matching prospect object
    if (this.sequence?.prospectListId && this.prospects && this.prospects.length > 0) {
      const matchingProspect = this.prospects.find((p: any) => 
        p.prospectListId === this.sequence!.prospectListId || 
        p.id === this.sequence!.prospectListId ||
        p.name === this.sequence!.prospectListId
      );
      if (matchingProspect) {
        this.newSequence.prospectListId = matchingProspect as any;
        console.log('Updated prospect selection:', matchingProspect);
      }
    }
  }

  private async loadProspectsIfNeeded(): Promise<void> {
    // Only load prospects if they're not provided by parent and we have a company
    if ((!this.prospects || this.prospects.length === 0) && this.currentCompany?.companyId) {
      console.log('Loading prospects in child component');
      this.prospectsLoading = true;
      
      try {
        const result = await this.prospectService.getProspectLists(this.currentCompany.companyId, 'email');
        if (result?.data?.lists) {
          const prospects = result.data.lists;
          console.log('Loaded prospects:', prospects);
          
          // Emit the loaded prospects to parent
          this.prospectsLoaded.emit(prospects);
          
          // Update prospect selection if we're in edit mode
          if (this.editingSequence && this.sequence) {
            // Temporarily set prospects for selection update
            this.prospects = prospects;
            this.updateProspectSelection();
          }
        } else {
          console.warn('No prospects data received');
        }
      } catch (error) {
        console.error('Error loading prospects in child component:', error);
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Could not load prospect lists'
        });
      } finally {
        this.prospectsLoading = false;
      }
    }
  }

  private initializeForm(): void {
    console.log(this.prospects);
    
    if (this.editingSequence && this.sequence) {
      this.newSequence = {
        sequenceId: this.sequence.sequenceId,
        name: this.sequence.name,
        emailAccounts: this.sequence.emailAccounts,
        description: this.sequence.description,
        industry: this.sequence.industry || '',
        prospectListId: this.sequence.prospectListId,
        steps: [
          {
            type: 'initial',
            templateId: null,
            abTestingEnabled: false
          }
        ], // Initialize with default step, will be replaced when steps load
        integration: this.sequence.provider
      };
      
      // Update prospect selection if prospects are available
      this.updateProspectSelection();
      
      // Set the selected integration for editing
      this.selectedIntegration = this.integrations.find(i => i.platform === this.sequence!.provider) || null;
      
      // Load steps for editing
      this.loadSequenceSteps();
      
      // Only fetch email accounts when editing if we have a selected integration
      if (this.selectedIntegration) {
        this.getEmailAccounts();
      }
    } else {
      this.resetForm();
      // Don't fetch email accounts here - wait for user to select an integration
    }
  }

  private resetForm(): void {
    this.newSequence = {
      name: '',
      description: '',
      industry: '',
      emailAccounts: [],
      steps: [
        {
          type: 'initial',
          templateId: null,
          abTestingEnabled: false,
          waitDuration: 1,
          waitUnit: 'days'
        }
      ],
      prospectListId: '',
      integration: ''
    };
    this.selectedPlatformEmailAccounts = [];
    this.hasTriedToSave = false;
    this.leadDistributionJobId = null;
    this.showLeadDistribution = false;
    this.sequenceCreated = false;
    
    // Reset job tracking properties
    this.jobProgress = 0;
    this.jobMessage = '';
    this.jobStatus = 'queued';
  }

  getEmailIntegrations(): Integration[] {
    const emailIntegrations = this.integrations?.filter(e => e.type?.includes('email')) || [];
    return emailIntegrations;
  }

  async getEmailAccounts(): Promise<void> {
    try {
      this.selectedPlatformEmailAccounts = [];
      if (this.selectedIntegration?.platform && this.currentCompany) {
        const result = await this.emailOutreachService.getPlatformEmailAccounts(
          this.currentCompany.companyId,
          this.selectedIntegration.platform
        );
        this.platformEmailAccounts = result.data;    
        // If we're in edit mode, select the matching email accounts
        if (this.editingSequence && this.sequence?.emailAccounts) {
          this.setSelectedEmailAccountsForEditing();
        }
      }

    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to fetch the email accounts for sending'
      });
    }
  }

  onIntegrationChange(): void {
    this.integrationChanged.emit(this.selectedIntegration!);
    this.getEmailAccounts();
    
    // Trigger template validation when platform changes
    this.validateTemplatesOnPlatformChange();
  }

  /**
   * Validate all templates when platform changes (non-blocking)
   */
  private async validateTemplatesOnPlatformChange(): Promise<void> {
    if (!this.selectedIntegration) return;

    const targetPlatform = this.selectedIntegration.platform as 'instantly' | 'smartlead' |  'hubspot';
    let hasIncompatibleTemplates = false;

    // Check all steps for template compatibility issues
    for (const step of this.newSequence.steps) {
      if (step.templateId) {
        const template = this.templates.find(t => t.id === step.templateId);
        if (template) {
          const validation = this.templateValidationService.validateTemplate(template, targetPlatform);
          if (!validation.isValid && (validation.conversions.length > 0 || validation.invalidVariables.length > 0)) {
            hasIncompatibleTemplates = true;
            break;
          }
        }
      }
    }

    // Show a warning message if there are incompatible templates
    if (hasIncompatibleTemplates) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Template Compatibility Warning',
        detail: `Some selected templates may not be compatible with ${this.selectedIntegration.platform}. Template variables will be validated when you save the sequence.`,
        life: 6000
      });
    }
  }

  private setSelectedEmailAccountsForEditing(): void {
    if (!this.sequence?.emailAccounts || !this.platformEmailAccounts) {
      return;
    }

    console.log('Setting selected email accounts for editing');
    console.log('Sequence email accounts:', this.sequence.emailAccounts);
    console.log('Platform email accounts:', this.platformEmailAccounts);

    // Match platform email accounts with sequence email accounts by email address
    this.selectedPlatformEmailAccounts = this.platformEmailAccounts.filter(platformAccount => {
      return this.sequence!.emailAccounts.some((sequenceAccount: any) => {
        // Match by email address (case insensitive)
        return platformAccount.email?.toLowerCase() === sequenceAccount.email?.toLowerCase();
      });
    });

    console.log('Selected platform email accounts:', this.selectedPlatformEmailAccounts);
  }

  private async loadSequenceSteps(): Promise<void> {
    if (this.sequence?.sequenceId && this.currentCompany?.companyId) {
      try {
        console.log('Loading steps for sequence:', this.sequence.sequenceId);
        const steps = await this.emailOutreachService.getEmailSequenceSteps(
          this.sequence.sequenceId, 
          this.currentCompany.companyId
        );
        console.log('Loaded steps:', steps);
        
        // Ensure we have at least one step
        if (steps && steps.length > 0) {
          this.newSequence.steps = steps;
          console.log('Steps loaded successfully:', this.newSequence.steps);
        } else {
          console.warn('No steps returned from API, using default step');
          // If no steps returned, keep the default initial step
          this.newSequence.steps = [
            {
              type: 'initial',
              templateId: null,
              abTestingEnabled: false,
              waitDuration: 1,
              waitUnit: 'days'
            }
          ];
        }
      } catch (error) {
        console.error('Error loading sequence steps:', error);
        // Keep default step if loading fails
        this.newSequence.steps = [
          {
            type: 'initial',
            templateId: null,
            abTestingEnabled: false,
            waitDuration: 1,
            waitUnit: 'days'
          }
        ];
      }
    } else {
      console.warn('Missing sequence ID or company ID for loading steps');
    }
  }

  get isEmailAccountsInvalid(): boolean {
    return !this.selectedPlatformEmailAccounts || this.selectedPlatformEmailAccounts.length === 0;
  }

  onEmailAccountsChange(): void {
    if (this.selectedPlatformEmailAccounts && this.selectedPlatformEmailAccounts.length > 0) {
      this.hasTriedToSave = false;
    }
  }

  getFollowUpSteps(): SequenceStep[] {
    return this.newSequence.steps.slice(1);
  }

  addSequenceStep(): void {
    const followUpNumber = this.newSequence.steps.length;
    this.newSequence.steps.push({
      type: `followup${followUpNumber}`,
      templateId: null,
      waitDuration: 1,
      waitUnit: 'days',
      abTestingEnabled: false
    });
  }

  removeStep(index: number): void {
    if (this.newSequence.steps.length > 1) {
      this.newSequence.steps.splice(index, 1);
      for (let i = 1; i < this.newSequence.steps.length; i++) {
        this.newSequence.steps[i].type = `followup${i}`;
      }
    }
  }

  /**
   * Convert plain text to basic HTML
   * Preserves newlines and basic formatting
   */
  private convertPlainTextToHtml(text: string): string {
    if (!text) return '';

    return text
      // Convert newlines to <br> tags
      .replace(/\n/g, '<br>')
      // Wrap in paragraph tag
      .split('<br><br>')
      .map(para => `<p>${para}</p>`)
      .join('');
  }

  async saveSequence(): Promise<void> {
    try {
      this.hasTriedToSave = true;

      // Validate required fields (quick checks first)
      if (!this.selectedIntegration) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please select a platform integration'
        });
        return;
      }

      if (this.isEmailAccountsInvalid) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please select at least one email account'
        });
        return;
      }

      if (!this.newSequence.name || this.newSequence.name.trim().length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please enter a sequence name'
        });
        return;
      }

      // Show loading stepper early (before async validation)
      this.isSaving = true;
      this.resetSaveSteps();
      this.updateSaveStep(0); // Validating step
      this.cdr.detectChanges();
      console.log('🎬 Save started - showing stepper at validation step');

      // Validate template compatibility (may show dialog)
      const templateValidationResult = await this.validateTemplateCompatibility();
      if (!templateValidationResult) {
        this.isSaving = false;
        this.resetSaveSteps();
        return; // User cancelled or validation failed
      }

      this.newSequence.integration = this.selectedIntegration?.platform || '';
      this.newSequence.emailAccounts = this.selectedPlatformEmailAccounts || [];

      // Populate template details for each step
      this.newSequence.steps.forEach((step, index) => {
        console.log(`Processing step ${index + 1}:`, {
          stepTemplateId: step.templateId,
          stepTemplateIdType: typeof step.templateId,
          availableTemplateIds: this.templates.map(t => ({ id: t.id, name: t.name }))
        });

        const temp = this.templates.find(temp => temp.id === step.templateId);
        console.log(`Found template for step ${index + 1}:`, temp);
        console.log(`Template body:`, temp?.body);
        console.log(`Template bodyHtml:`, temp?.bodyHtml);
        console.log(`Template has bodyHtml?`, !!temp?.bodyHtml);

        step.templateName = temp?.name;
        step.templateSubject = temp?.subject;
        // Prioritize HTML version for templateBody, fall back to plain text
        step.templateBody = temp?.bodyHtml || temp?.body;
        step.templateBodyHtml = temp?.bodyHtml; // Keep HTML version separate for reference

        console.log(`Assigned templateBody (should be HTML):`, step.templateBody);
        console.log(`Assigned templateBodyHtml:`, step.templateBodyHtml);

        // Debug logging to verify A/B test data is preserved
        if (step.abTestingEnabled) {
          // Convert AB variant body plain text to HTML if provided
          if (step.abVariantBody) {
            step.abVariantBodyHtml = this.convertPlainTextToHtml(step.abVariantBody);
          }

          console.log(`Step ${index + 1} A/B testing data:`, {
            abTestingEnabled: step.abTestingEnabled,
            abVariantSubject: step.abVariantSubject,
            abVariantBody: step.abVariantBody,
            abVariantBodyHtml: step.abVariantBodyHtml,
            abVariantCTA: step.abVariantCTA
          });
        }
      });

      this.hasTriedToSave = false;

      // Debug logging - show complete sequence data being sent to API
      console.log('Complete sequence data being sent to API:', JSON.stringify(this.newSequence, null, 2));

      if (this.editingSequence) {
        this.updateSaveStep(1); // Creating Campaign (skip validation since it's done)
        this.loadingMessage = 'Updating sequence...';
        await this.emailOutreachService.updateEmailSequence(this.newSequence, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Sequence updated successfully'
        });
        this.updateSaveStep(2); // Configuring
        await this.sleep(1000);
        this.loadingMessage = 'Syncing with integration platforms...';
        await this.emailOutreachService.updateEmailSequenceInPlatforms(
          this.newSequence.sequenceId || '',
          this.currentCompany!.companyId
        );
      } else {
        // Move to step 1: Creating Campaign (validation already complete)
        this.updateSaveStep(1);

        // Simulate progress during API call (since backend doesn't send progress updates)
        let progressInterval: any = null;
        try {
          progressInterval = setInterval(() => {
            if (this.currentSaveStep < 3 && this.isSaving) {
              this.updateSaveStep(this.currentSaveStep + 1);
            }
          }, 2500); // Move to next step every 2.5 seconds

          // Single unified API call that handles everything
          const createResponse = await this.emailOutreachService.createSequenceWithPlatform(
            this.newSequence,
            this.selectedIntegration?.platform,
            this.currentCompany!.companyId
          );

          // Clear progress simulation
          if (progressInterval) clearInterval(progressInterval);

          // Complete all steps
          this.updateSaveStep(3); // Finalizing
          this.saveSteps.forEach(step => step.completed = true);

          console.log('Create sequence response:', createResponse);
          console.log('Full API response structure:', JSON.stringify(createResponse, null, 2));

          // Mark sequence as created
          this.sequenceCreated = true;

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Sequence created successfully!'
          });

          // Check if we have a lead distribution job (new async API)
        // The API can return job data either directly in data object or nested in leadDistributionJob
        const jobId = createResponse?.data?.leadDistributionJob?.jobId || 
                     (createResponse?.data?.jobId && createResponse?.data?.type === 'lead_distribution' ? createResponse.data.jobId : null);
        
        if (jobId) {
          this.leadDistributionJobId = jobId;
          this.showLeadDistribution = true;
          
          // Hide loading overlay now that sequence is created - lead distribution component will show progress
          this.isSaving = false;
          this.loadingMessage = '';
          
          console.log('Lead distribution job detected:', {
            jobId: jobId,
            showLeadDistribution: this.showLeadDistribution,
            responseData: createResponse?.data
          });
          
          // Check if the job is already completed or failed in the initial response
          const initialJobStatus = createResponse?.data?.leadDistributionJob?.status;
          if (initialJobStatus === 'completed') {
            console.log('⚡ Job already completed in initial response, handling immediately');
            // Give a brief moment for the modal to render, then complete
            setTimeout(() => {
              this.onLeadDistributionCompleted(createResponse.data.leadDistributionJob as JobStatus);
            }, 500);
          } else if (initialJobStatus === 'failed') {
            console.log('❌ Job already failed in initial response, handling immediately');
            // Give a brief moment for the modal to render, then show failure
            setTimeout(() => {
              this.onLeadDistributionFailed(createResponse.data.leadDistributionJob as JobStatus);
            }, 500);
          }
          
          // Set timeout to prevent UI from being stuck forever
          this.leadDistributionTimeout = setTimeout(() => {
            console.warn('Lead distribution timeout reached');
            
            const timeoutJob: JobStatus = {
              jobId: this.leadDistributionJobId!,
              type: 'lead_distribution',
              status: 'failed',
              progress: 0,
              message: 'Lead distribution timed out',
              error: 'The lead distribution process took too long and was cancelled. Your sequence was created successfully.',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
            
            this.onLeadDistributionFailed(timeoutJob);
          }, this.LEAD_DISTRIBUTION_TIMEOUT_MS);
        } else {
          // Old API response or no lead distribution needed, complete the process immediately
          console.log('No async job returned, completing sequence creation');
          console.log('No job detected. API response data:', createResponse?.data);
          this.handleSequenceCreationComplete();
        }

          // Show additional success messages for prospect list association if it happened
          if (this.newSequence.prospectListId && (this.newSequence.prospectListId as any)?.prospectListId?.length > 0) {
            this.messageService.add({
              severity: 'info',
              summary: `Prospect List Selected`,
              detail: `Prospects from ${(this.newSequence.prospectListId as any)?.name} will be distributed to your ${this.newSequence.name} sequence.`
            });
          }
        } finally {
          // Ensure progress interval is cleared on any exit
          if (progressInterval) clearInterval(progressInterval);
        }
      }

      // Only emit sequenceSaved and reset if no lead distribution is happening
      if (!this.showLeadDistribution) {
        this.sequenceSaved.emit();
        this.resetForm();
      }
    } catch (error: any) {
      console.error('Error saving sequence:', error);
      
      if (error.error?.error) {
        // Backend provided specific error message
        this.messageService.add({
          severity: 'error',
          summary: 'Error Creating Sequence',
          detail: error.error.error
        });
      } else if (error.error?.message) {
        // Alternative error message format
        this.messageService.add({
          severity: 'error',
          summary: 'Error Creating Sequence', 
          detail: error.error.message
        });
      } else if (error.message) {
        // Direct error message
        this.messageService.add({
          severity: 'error',
          summary: 'Error Creating Sequence',
          detail: error.message
        });
      } else {
        // Fallback generic message
        this.messageService.add({
          severity: 'error',
          summary: 'Error Creating Sequence',
          detail: 'Failed to create sequence. Please check your platform integration settings and try again.'
        });
      }
    } finally {
      // Only reset loading state if no lead distribution is happening
      // If lead distribution is active, isSaving was already set to false above
      if (!this.showLeadDistribution) {
        this.isSaving = false;
        this.loadingMessage = '';
        this.resetSaveSteps();
      }
    }
  }

  cancelSequenceCreation(): void {
    this.resetForm();
    this.cancelled.emit();
  }

  onLeadDistributionCompleted(job: JobStatus): void {
    console.log('✅ PARENT: onLeadDistributionCompleted called with job:', job);
    console.log('✅ PARENT: Current showLeadDistribution state:', this.showLeadDistribution);
    
    // Clear timeout since job completed successfully
    if (this.leadDistributionTimeout) {
      clearTimeout(this.leadDistributionTimeout);
      this.leadDistributionTimeout = null;
    }
    
    this.messageService.add({
      severity: 'success',
      summary: 'Lead Distribution Complete',
      detail: `Successfully distributed ${job.results?.leadsProcessed || 0} leads to your sequence.`
    });

    this.handleSequenceCreationComplete();
  }

  onLeadDistributionFailed(job: JobStatus): void {
    console.log('❌ PARENT: onLeadDistributionFailed called with job:', job);
    
    // Clear timeout since job failed/completed
    if (this.leadDistributionTimeout) {
      clearTimeout(this.leadDistributionTimeout);
      this.leadDistributionTimeout = null;
    }
    
    // Show detailed error message
    let errorDetail = 'Lead distribution encountered errors. Your sequence was created but leads were not distributed.';
    if (job.error) {
      errorDetail = `Lead distribution failed: ${job.error}`;
    } else if (job.message) {
      errorDetail = `Lead distribution failed: ${job.message}`;
    }
    
    this.messageService.add({
      severity: 'error',
      summary: 'Lead Distribution Failed',
      detail: errorDetail,
      life: 8000 // Show for longer to ensure user sees it
    });

    // Also show that the sequence was still created successfully
    this.messageService.add({
      severity: 'info',
      summary: 'Sequence Created',
      detail: 'Your email sequence was created successfully, but lead distribution failed. You can manually distribute leads later.',
      life: 6000
    });

    this.handleSequenceCreationComplete();
  }

  private handleSequenceCreationComplete(): void {
    console.log('handleSequenceCreationComplete called');
    console.log('Before - showLeadDistribution:', this.showLeadDistribution);
    
    // Clear timeout if still active
    if (this.leadDistributionTimeout) {
      clearTimeout(this.leadDistributionTimeout);
      this.leadDistributionTimeout = null;
    }
    
    this.isSaving = false;
    this.loadingMessage = '';
    this.showLeadDistribution = false;
    this.leadDistributionJobId = null;
    this.sequenceCreated = false;
    
    console.log('After - showLeadDistribution:', this.showLeadDistribution);
    
    // Force change detection to ensure UI updates
    this.cdr.markForCheck();
    
    this.sequenceSaved.emit();
    this.resetForm();
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Update save progress step
   */
  private updateSaveStep(stepIndex: number): void {
    // Mark all previous steps as completed
    this.saveSteps.forEach((step, index) => {
      step.completed = index < stepIndex;
      step.active = index === stepIndex;
    });
    this.currentSaveStep = stepIndex;
    this.loadingMessage = this.saveSteps[stepIndex]?.description || '';
    this.cdr.detectChanges(); // Force UI update for progress changes
  }

  /**
   * Reset save steps to initial state
   */
  private resetSaveSteps(): void {
    this.saveSteps.forEach(step => {
      step.completed = false;
      step.active = false;
    });
    this.currentSaveStep = 0;
  }

  /**
   * Update progress from LeadDistributionStatusComponent updates
   */
  updateProgressFromJob(job: JobStatus): void {
    console.log('📊 Updating progress from job status:', job);
    
    // Update progress tracking properties
    this.jobProgress = Math.max(this.jobProgress, job.progress || 0);
    this.jobMessage = job.message || 'Processing...';
    this.jobStatus = job.status;
    this.loadingMessage = this.jobMessage;
    
    // Force change detection
    this.cdr.markForCheck();
    
    console.log('💾 Updated UI state:', {
      progress: this.jobProgress,
      message: this.jobMessage,
      status: this.jobStatus
    });
  }

  /**
   * Check if user has configured their settings
   * Shows error message if settings are missing
   */
  private async checkUserSettings(): Promise<void> {
    try {
      const userId = this.authService.userId();
      if (!userId) {
        console.warn('No user ID available for settings check');
        return;
      }

      console.log('Checking user settings for user:', userId);
      
      const userSettingsResponse = await firstValueFrom(
        this.userSettingsService.getUserSettings(userId)
      ) as UserSettings;
      
      if (!userSettingsResponse) {
        this.showUserSettingsError();
        return;
      }

      // Check if user has configured essential settings
      //@ts-ignore
      if (!this.hasRequiredSettings(userSettingsResponse.data)) {
        this.showUserSettingsError();
      }

    } catch (error: any) {
      console.log('User settings check error:', error);
      
      // If 404, user hasn't set up settings yet
      if (error.status === 404 || error.error?.statusCode === 404) {
        this.showUserSettingsError();
      } else {
        console.error('Error checking user settings:', error);
        // Don't show error for other types of failures - just log them
      }
    }
  }

  /**
   * Check if user settings contain required fields
   */
  private hasRequiredSettings(settings: UserSettings): boolean {
    if (!settings) {
      return false;
    }

    // Check for essential outreach settings
    const hasScheduleSettings = typeof settings.scheduledOutreach === 'boolean' &&
                               Boolean(settings.startTime) &&
                               Boolean(settings.endTime) &&
                               Boolean(settings.timeZone) &&
                               Array.isArray(settings.selectedDays) &&
                               settings.selectedDays.length > 0;

    return hasScheduleSettings;
  }

  /**
   * Show error message about missing user settings
   */
  private showUserSettingsError(): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'User Settings Required',
      detail: 'Please configure your outreach schedule in User Settings before creating sequences. This ensures your sequences follow your preferred sending times.',
      life: 8000
    });
  }

  /**
   * Generate a temporary ID for fallback purposes
   */
  private generateTempId(): string {
    return 'temp_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  }

  /**
   * Validate template compatibility with the selected platform
   */
  private async validateTemplateCompatibility(): Promise<boolean> {
    if (!this.selectedIntegration) {
      return true; // No platform selected, skip validation
    }

    const targetPlatform = this.selectedIntegration.platform as 'instantly' | 'smartlead' | 'hubspot';
    
    // Check each step's template
    for (let i = 0; i < this.newSequence.steps.length; i++) {
      const step = this.newSequence.steps[i];
      
      if (step.templateId) {
        const template = this.templates.find(t => t.id === step.templateId);
        
        if (template) {
          const validation = this.templateValidationService.validateTemplate(template, targetPlatform);
          
          if (!validation.isValid && (validation.conversions.length > 0 || validation.invalidVariables.length > 0)) {
            // Show dialog for this incompatible template
            const dialogResult = await this.showTemplateConversionDialog(template, validation, targetPlatform);
            
            if (dialogResult?.action === 'cancel') {
              return false; // User cancelled
            } else if (dialogResult?.action === 'create' && dialogResult.template) {
              // Create new template and update step
              const newTemplate = await this.createConvertedTemplate(dialogResult.template);
              if (newTemplate && newTemplate.id) {
                console.log('Updating step with new template:', {
                  templateId: newTemplate.id,
                  templateName: newTemplate.name
                });

                // Update the step with the new template
                step.templateId = newTemplate.id;
                step.templateName = newTemplate.name;
                step.templateSubject = newTemplate.subject;
                step.templateBody = newTemplate.body;
                
                // Add new template to templates list and emailTemplates dropdown
                this.templates.push(newTemplate);
                
                // Ensure the dropdown option has the correct structure
                const dropdownOption = {
                  label: newTemplate.name,
                  value: newTemplate.id // Keep as string to match DynamoDB schema
                };
                
                console.log('Adding to emailTemplates dropdown:', dropdownOption);
                this.emailTemplates.push(dropdownOption);

                // Force change detection to update the UI
                this.cdr.detectChanges();
              } else {
                console.error('Failed to create template or template missing ID');
                return false;
              }
            }
          }
        }
      }
    }
    
    return true;
  }

  /**
   * Show template conversion dialog
   */
  private async showTemplateConversionDialog(
    template: TemplateModel, 
    validation: any, 
    targetPlatform: 'instantly' | 'smartlead' | 'hubspot'
  ): Promise<any> {
    const newTemplateName = this.templateValidationService.getConvertedTemplateName(
      template.name, 
      targetPlatform
    );

    return new Promise((resolve) => {
      const dialogRef = this.dialogService.open(TemplateConversionDialogComponent, {
        header: 'Template Variable Incompatibility',
        width: '700px',
        modal: true,
        closable: false,
        data: {
          templateName: template.name,
          targetPlatform,
          validationResult: validation,
          originalTemplate: template,
          newTemplateName
        }
      });

      dialogRef.onClose.subscribe((result: any) => {
        resolve(result || { action: 'cancel' });
      });
    });
  }

  /**
   * Create a new template with converted variables
   */
  private async createConvertedTemplate(templateData: Partial<TemplateModel>): Promise<TemplateModel | null> {
    try {
      if (!this.currentCompany) {
        throw new Error('No company selected');
      }

      const newTemplate: any = {
        name: templateData.name,
        subject: templateData.subject,
        body: templateData.body,
        type: templateData.type || 'email'
      };

      console.log('Creating template with data:', newTemplate);
      const response = await this.emailOutreachService.createEmailTemplate(newTemplate, this.currentCompany.companyId);
      console.log('Template creation response:', response);
      
      this.messageService.add({
        severity: 'success',
        summary: 'Template Created',
        detail: `Converted template "${templateData.name}" has been created successfully.`
      });

      // Handle different response structures
      let createdTemplate: any;
      if (Array.isArray(response)) {
        createdTemplate = response[0];
      } else if (response && (response as any).data) {
        // If response has a data wrapper
        const responseData = (response as any).data;
        createdTemplate = Array.isArray(responseData) ? responseData[0] : responseData;
      } else {
        createdTemplate = response;
      }

      console.log('Processed template:', createdTemplate);

      // Ensure we have the required fields and proper ID type
      const templateId = createdTemplate?.id || this.generateTempId();
      
      const templateModel: TemplateModel = {
        id: templateId,
        name: createdTemplate?.name || templateData.name!,
        subject: createdTemplate?.subject || templateData.subject!,
        body: createdTemplate?.body || templateData.body!,
        type: createdTemplate?.type || 'email',
        createdDate: createdTemplate?.createdDate,
        lastModified: createdTemplate?.lastModified
      };

      console.log('Final template model:', templateModel);
      return templateModel;
      
    } catch (error: any) {
      console.error('Error creating converted template:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Template Creation Failed',
        detail: `Failed to create converted template: ${error.message || 'Unknown error'}`
      });
      return null;
    }
  }
}